import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AIServices } from '../ai/ai-services';
import { AnalysisScope, filterForScope, isWholeOrganisation, modelsInScope, scopeForLinqset } from '../analysis/scope';
import { Change, changeFormat, changeFrom, measure } from '../analysis/comparison';
import { ContextGraphButtonComponent } from '../analysis/context-graph-button.component';
import { ContextSource } from '../analysis/context-graph.service';
import { NodeCorrection, NodeCorrectionService } from '../analysis/node-correction.service';
import { EditInModelComponent, HeldAsFixComponent, ValueFixComponent } from '../analysis/node-fixes.component';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { AIEpmValueSuggestion, EpmDecisionEvidence } from '../common/epm-decisions';
import { heldOutsideSystems, InformationHeld, INFORMATION_HELD_LABELS } from '../common/information-value';
import { CURRENT_STATE_PROJECT_KEY, getInsights, Linqset, NODE_SUPERTYPE, NODE_TYPE, OUTPUT_VALUE_SOURCE } from '../common/linqset-model';
import { ASSET_CLASSIFICATION_LABELS, Currency, DEFAULT_CURRENCY, defaultCapacityHours, getCurrency } from '../common/organisation';
import { MatDialog } from '@angular/material/dialog';
import { editInformationAsset } from '../model-components/information-asset/information-asset-dialog.component';
import { OrganisationService } from '../organisation/organisation.service';
import { DownloadService } from '../model-services/download.service';
import { toCsv } from '../model-services/csv';
import { v4 } from 'uuid';
import { AssetLinkSuggestion } from './asset-links';
import { buildEpmScorecard, epmComponents } from '../epm/epm-scorecard-model';
import { FormattingService } from '../linq-utils/formatting/formatting.service';
import { InsightsService } from '../model-services/insights.service';
import { systemKindOf } from '../common/system-kind';
import { assetInputs, buildInformationAnalysis, HELD_ORDER, InformationAnalysis, InformationAnalysisInput, InformationAssetSummary, InformationOutcome, InformationOutput, InformationState, OutcomeState, InformationStep, InformationTotals, ValueHolder } from './information-analysis-model';

// the headline figures, as they're compared
const informationFigures = (t: InformationTotals) => ({
    outputs: measure(t.outputs, 'none', 'count'),
    value: measure(t.value, 'up', 'number'),
    valued: measure(t.withValue, 'up', 'count'),
    inUse: measure(t.inUseOnly, 'down', 'count'),
    outside: measure(t.heldOutside.share * 100, 'down', 'percent'),
    friction: t.frictionCost ? measure(t.frictionCost, 'down', 'money') : measure(t.frictionHours, 'down', 'hours'),
});


const sameName = (a: string | null | undefined, b: string | null | undefined) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// a table's sort: the column chosen and which way. A column's first click sorts text A to Z and figures largest first;
// clicking it again reverses it. Rows that tie are in name order
class TableSort<C extends string> {
    column: C | null = null;
    descending = false;

    constructor(private textColumns: Set<C>) {}

    by(column: C) {
        if (this.column === column) this.descending = !this.descending;
        else {
            this.column = column;
            this.descending = !this.textColumns.has(column);
        }
    }

    aria(column: C) {
        return this.column !== column ? 'none' : this.descending ? 'descending' : 'ascending';
    }

    icon(column: C) {
        return this.column !== column ? 'unfold_more' : this.descending ? 'arrow_downward' : 'arrow_upward';
    }

    sort<T extends { name: string }>(rows: T[], keyOf: (row: T, column: C) => string | number): T[] {
        const column = this.column;
        if (!column) return rows;
        const direction = this.descending ? -1 : 1;
        return [...rows].sort((a, b) => {
            const x = keyOf(a, column), y = keyOf(b, column);
            const order = typeof x === 'string' ? x.localeCompare(y as string, undefined, { sensitivity: 'base', numeric: true }) : x - (y as number);
            return direction * order || a.name.localeCompare(b.name);
        });
    }
}

// the outputs table's columns it can be sorted by
type OutputSortColumn = 'name' | 'model' | 'items' | 'value' | 'revenuePerItem' | 'derived' | 'held' | 'suggested' | 'revenue' | 'uses' | 'cost' | 'state';
const OUTPUT_TEXT_COLUMNS = new Set<OutputSortColumn>(['name', 'model', 'held']);
// states from least to most valued, so sorting by state, largest first, puts the most valued first
const OUTPUT_STATE_ORDER: InformationState[] = ['Dead end', 'In use', 'Valued', 'Revenue bearing'];
// and the outcomes table's
type OutcomeSortColumn = 'name' | 'model' | 'items' | 'value' | 'revenuePerItem' | 'valueYear' | 'revenue' | 'realisedBy' | 'cost' | 'state';
const OUTCOME_TEXT_COLUMNS = new Set<OutcomeSortColumn>(['name', 'model']);
const OUTCOME_STATE_ORDER: OutcomeState[] = ['Not valued', 'Valued', 'Revenue bearing'];

// The information of what is looked at, the infonomics way: the information assets the outputs are states of, and every
// output with a value. A user sets it, or LINQ derives it from how the output is used (the revenue and outcomes it reaches, its reuse, the other
// models and external organisations using it). The view shows the outputs and their values, what the derived values
// rest on, where the value lands (the roles and systems credited with it), the friction behind outputs nothing uses,
// and lets the user set a value from LINQ's or AI's suggestion
@Component({
    selector: 'app-information-analysis',
    standalone: true,
    imports: [DecimalPipe, NgTemplateOutlet, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, ContextGraphButtonComponent, EditInModelComponent, HeldAsFixComponent,
        ValueFixComponent],
    template: `
        @if (loading) {
        <div class="status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Valuing the information…</span></div>
        }@else if (error) {
        <p class="status error" role="alert">{{ error }}</p>
        }@else if (analysis; as a) {
        <div class="info" data-testid="information-analysis">
            <p class="intro">Infonomics treats information as an asset: it isn't used up by being used, so the more places an output reaches, the more it is
                worth. Every output carries a value per item, set by a user or <b>derived by LINQ from how it is used</b> (0 to 100 across the organisation).
                LINQ multiplies it by the items produced a year and passes it back to the actions, roles and systems that produce the output, so value
                shows up where it is created. An output in use is never friction; only dead ends nothing uses are. Where outputs and the information passed
                between steps are linked to an <b>information asset</b>, the asset is valued too, across all its states.</p>

            @if (!a.outputs.length) {
            <section class="panel empty" data-testid="information-none">
                <mat-icon aria-hidden="true">inventory_2</mat-icon>
                <div>
                    <strong>No outputs yet</strong>
                    <p>An output is the last piece of information in a model, or one shared with other models. Add information to the models looked at to value it here.</p>
                </div>
            </section>
            } @else {

            <!-- the headline figures -->
            <section class="tiles" aria-label="Headline figures">
                <div class="tile">
                    <span class="label"><mat-icon aria-hidden="true">inventory_2</mat-icon>Outputs</span>
                    <strong>{{ a.totals.outputs | number }}</strong>@if (vs('outputs'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ count(a.totals.items) }} items a year@if (a.totals.assets) {, {{ a.totals.linkedOutputs }} linked to {{ a.totals.assets }} {{ a.totals.assets === 1 ? 'asset' : 'assets' }}}</span>
                </div>
                <div class="tile" matTooltip="Value per item set on outputs and outcomes, times the items produced a year, counted once across the models">
                    <span class="label"><mat-icon aria-hidden="true">diamond</mat-icon>Value produced</span>
                    <strong>{{ count(a.totals.value) }}</strong>@if (vs('value'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ a.totals.revenue ? money(a.totals.revenue) + ' of revenue a year' : 'no revenue on outputs' }}</span>
                </div>
                <div class="tile" matTooltip="Outputs with a value set by a user, or accepted from a suggestion, or carrying revenue">
                    <span class="label"><mat-icon aria-hidden="true">verified</mat-icon>Valued</span>
                    <strong>{{ a.totals.withValue }}<span class="of"> of {{ a.totals.outputs }}</span></strong>@if (vs('valued'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ a.totals.setByHand }} typed, {{ a.totals.accepted }} accepted from a suggestion</span>
                </div>
                <div class="tile" [class.warn]="a.totals.inUseOnly > 0" matTooltip="Outputs that are used, so LINQ derived a value for them, but no one has set one">
                    <span class="label"><mat-icon aria-hidden="true">auto_graph</mat-icon>Valued by use only</span>
                    <strong>{{ a.totals.inUseOnly }}</strong>@if (vs('inUse'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ a.totals.inUseHours ? hours(a.totals.inUseHours) + ' hours' + (a.totals.inUseCost ? ' and ' + money(a.totals.inUseCost) : '') + ' a year kept out of friction' : 'their worth rests on the derived value' }}</span>
                </div>
                <div class="tile" [class.warn]="a.totals.heldOutside.outputs > 0" data-testid="information-held-outside"
                    matTooltip="Of the outputs whose type or system says how they are held, the share of the derived value, per item times the items a year, in those kept in local tools (spreadsheets, documents, email), as files, on paper or passed on by word of mouth. Outputs typed as plain information with no system attached are left out. It doesn't change the derived value: it says where valuable information sits outside a controlled system">
                    <span class="label"><mat-icon aria-hidden="true">folder_off</mat-icon>Value held outside systems</span>
                    <strong>{{ percent(a.totals.heldOutside.share) }}</strong>@if (vs('outside'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ heldOutsideText(a.totals) }}@if (a.totals.assetsHeldOutside) {; {{ a.totals.assetsHeldOutside }} {{ a.totals.assetsHeldOutside === 1 ? 'asset has' : 'assets have' }} a state outside}</span>
                </div>
                <div class="tile" [class.bad]="a.totals.frictionHours > 0 || a.totals.frictionCost > 0" [class.good]="!a.totals.frictionHours && !a.totals.frictionCost"
                    matTooltip="Friction as the EPM scorecard counts it: the work behind outputs nothing uses; exception work, the steps items take when they leave the main flow and return to it later; work marked as not adding value; re-keying paper or spoken information into a system; and the manual bridges carrying information between systems. A step that is more than one of these is counted once">
                    <span class="label"><mat-icon aria-hidden="true">block</mat-icon>Friction</span>
                    <strong>{{ a.totals.frictionCost ? money(a.totals.frictionCost) : a.totals.frictionHours ? hours(a.totals.frictionHours) + ' h' : money(0) }}</strong>@if (vs('friction'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ frictionSplit(a.totals) }}</span>
                </div>
            </section>

            <div class="body">
                <!-- how the values are derived -->
                <section class="panel signals" aria-labelledby="info-signals">
                    <h3 id="info-signals">How LINQ derives a value</h3>
                    <p class="note">Six signals read from the graph, each per item and measured against the organisation's highest, weighted and scaled so the most valuable information is 100.
                        A signal no output has is left out. The output with the most of each signal:</p>
                    <ul class="signal-list" data-testid="information-signals">
                        @for (s of a.signals; track s.key) {
                        <li [matTooltip]="s.description">
                            <span class="signal-head"><strong>{{ s.label }}</strong><span class="weight" matTooltip="Its weight in the derived value">× {{ s.weight }}</span></span>
                            <span class="track"><span class="fill" [style.width.%]="s.weight / maxWeight * 100"></span></span>
                            <span class="leader">
                                @if (s.leader; as l) {
                                <button type="button" class="link" (click)="selectByName(l.name, l.model)">{{ l.name }}</button>
                                <span class="muted">{{ s.unit === 'per item' ? (s.key === 'revenue' ? money(l.amount) : count(l.amount)) + ' per item' : (l.amount | number) }}</span>
                                } @else {<span class="muted">none yet</span>}
                            </span>
                        </li>
                        }
                    </ul>
                </section>

                <!-- who creates the value -->
                <section class="panel holders" aria-labelledby="info-holders">
                    <h3 id="info-holders">Who creates the value</h3>
                    <p class="note">Each role's and system's share of the value its work creates: an output's value is shared between the value adding steps producing it by their work, and each step's share between its roles, and separately its systems. So the roles' add up, and the systems', to the value of the work they do. Ranked by revenue where there is any.</p>
                    @if (a.holders.length) {
                    <ul class="holder-list" data-testid="information-holders">
                        @for (h of a.holders.slice(0, holdersShown); track h.key) {
                        <li [matTooltip]="holderTip(h)">
                            <span class="holder-head"><span class="kind" [class.kind-system]="h.kind === 'System'">{{ h.kind }}</span><strong>{{ h.name }}</strong>@if (contextScope) {<app-context-graph-button [organisation]="linqset!.organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: h.kind === 'System' ? 'system' : 'role', key: h.key, name: h.name}}" [name]="h.name"></app-context-graph-button>}
                                @if (h.outputsSupported) {<span class="muted">{{ h.outputsSupported }} {{ h.outputsSupported === 1 ? 'output' : 'outputs' }}</span>}</span>
                            <span class="track"><span class="fill" [style.width.%]="holderWidth(h)"></span></span>
                            <span class="muted amount">{{ holderAmount(h) }}</span>
                        </li>
                        }
                    </ul>
                    @if (a.holders.length > holdersShown) {<button mat-button color="accent" (click)="holdersShown = a.holders.length">Show all {{ a.holders.length }}</button>}
                    } @else {
                    <p class="none">No role's or system's work creates value yet: set a value or revenue on an output, or accept a derived value, and it is shared between the work producing it.</p>
                    }
                </section>
            </div>

            <!-- how the information is held -->
            @if (a.held.length) {
            <section class="panel held-panel" aria-labelledby="info-held">
                <h3 id="info-held">How the information is held</h3>
                <p class="note">From each piece of information's type and the systems attached to it. Information typed as plain information is read by LINQ from its systems and name, where they say: confirm those reads in the outputs below, or on the EPM scorecard. Held outside a controlled system, it can be lost, or a copy used as if it were the real thing.</p>
                <div class="scroll">
                    <table class="list held-table" data-testid="information-held">
                        <thead><tr><th>Held</th><th class="num">Outputs</th><th class="num" matTooltip="Of the outputs' derived value, per item times the items a year">Share of derived value</th><th class="num" matTooltip="Pieces of information passed from one step to another">Hand-offs</th></tr></thead>
                        <tbody>
                            @for (h of a.held; track h.held) {
                            <tr [class.outside]="isHeldOutside(h.held)">
                                <td class="first">{{ heldKindLabel(h.held) }}</td>
                                <td class="num">{{ h.outputs || '–' }}@if (h.outputsRead) {<span class="read" matTooltip="LINQ's read: typed as plain information"> ({{ h.outputsRead }} read)</span>}</td>
                                <td class="num">{{ h.outputs ? percent(h.derivedShare) : '–' }}</td>
                                <td class="num">{{ h.handoffs || '–' }}@if (h.handoffsRead) {<span class="read" matTooltip="LINQ's read: typed as plain information"> ({{ h.handoffsRead }} read)</span>}</td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </section>
            }

            <!-- the information assets -->
            @if (a.assets.length) {
            <section class="panel assets" aria-labelledby="info-assets">
                <div class="head">
                    <h3 id="info-assets">Information assets</h3>
                    <button mat-stroked-button color="accent" (click)="exportRegister()" matTooltip="Download the register of the assets shown, with their figures, as a CSV file" data-testid="information-export-register">
                        <mat-icon>download</mat-icon>Export register</button>
                </div>
                @if (a.totals.assetsAtRisk) {<p class="note risk-note" data-testid="information-assets-at-risk"><mat-icon aria-hidden="true">warning</mat-icon>{{ a.totals.assetsAtRisk }} {{ a.totals.assetsAtRisk === 1 ? 'asset is' : 'assets are' }} at risk from {{ a.totals.assetsAtRisk === 1 ? 'its' : 'their' }} register or how {{ a.totals.assetsAtRisk === 1 ? 'its' : 'their' }} states are held. Pick one to see why.</p>}
                <p class="note">The information itself, which outputs and the information passed between steps are states of. Its derived value is read across all its states and ranked against the other assets (100 is the most valuable); the work moving it from one state to the next isn't counted as use. Pick one to see its states.</p>
                <div class="scroll">
                    <table class="list" data-testid="information-assets">
                        <thead><tr>
                            <th>Asset</th><th class="optional">Owner</th><th class="optional">Class</th><th class="num">States</th><th class="num">Items a year</th><th class="num derived">Derived value</th><th class="optional">Held</th>
                            <th class="num optional">Revenue</th><th class="num optional">Uses</th><th class="num optional">Cost of its states</th><th>State</th>
                        </tr></thead>
                        <tbody>
                            @for (s of a.assets; track s.key) {
                            <tr [class.selected]="s.key === selectedAssetKey" (click)="selectAsset(s)" [attr.data-testid]="'information-asset-' + s.key">
                                <td class="first" [matTooltip]="s.description"><span class="with-context">{{ s.name }}@if (s.risks.length) {<mat-icon class="risk" [matTooltip]="riskTip(s)" aria-label="At risk">warning</mat-icon>}</span></td>
                                <td class="optional model">{{ s.owner || '–' }}</td>
                                <td class="optional model">{{ classLabel(s) }}</td>
                                <td class="num" [matTooltip]="statesTip(s)">{{ s.numberOfStates }}{{ s.models > 1 ? ' · ' + s.models + 'm' : '' }}</td>
                                <td class="num">{{ count(s.items) }}</td>
                                <td class="num derived">
                                    @if (s.derivedValue !== undefined) {
                                    <span class="bar"><span class="mini"><span class="fill" [style.width.%]="s.derivedValue"></span></span><span class="mini-value">{{ s.derivedValue > 0 ? s.derivedValue : '–' }}</span></span>
                                    } @else {<span class="muted" matTooltip="Worked out the next time the insights are worked out">not yet</span>}
                                </td>
                                <td class="optional held" [class.outside]="s.heldOutside > 0" [matTooltip]="assetHeldTip(s)">{{ assetHeldLabel(s) }}</td>
                                <td class="num optional">{{ s.revenue ? money(s.revenue) : '–' }}</td>
                                <td class="num optional" [matTooltip]="assetUses(s)">@if (s.derivedValue !== undefined) {<span>{{ s.consumers }}{{ s.consumerModels ? ' · ' + s.consumerModels + 'm' : '' }}</span>} @else {<span class="muted">–</span>}</td>
                                <td class="num optional">{{ s.cost ? money(s.cost) : hours(s.hours) + ' h' }}</td>
                                <td><span [class]="'state ' + stateClass(s)">{{ s.state }}</span></td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>

                <!-- the chosen asset -->
                @if (selectedAsset; as s) {
                <div class="drill" data-testid="information-asset-detail">
                    <div class="drill-head">
                        <h4>{{ s.name }}</h4>
                        @if (canApply) {<button type="button" class="link" (click)="editAsset(s)" data-testid="information-edit-asset">Edit its register<mat-icon>edit</mat-icon></button>}
                    </div>
                    @if (s.description) {<p class="note">{{ s.description }}</p>}
                    <p class="note register" data-testid="information-asset-register">
                        Owner <b>{{ s.owner || 'none' }}</b> · Classification <b>{{ classLabel(s) }}</b> · System of record <b>{{ s.systemOfRecord || 'none set' }}</b>
                    </p>
                    @if (s.risks.length) {<ul class="risks" data-testid="information-asset-risks">@for (r of s.risks; track r.kind) {<li><mat-icon aria-hidden="true">warning</mat-icon>{{ r.text }}</li>}</ul>}
                    <div class="drill-body">
                        <div>
                            <h5>Its derived value, {{ s.derivedValue === undefined ? 'not worked out yet' : s.derivedValue > 0 ? s.derivedValue + ' of 100' : 'none' }}</h5>
                            <p class="note">{{ assetReasons(s) }}</p>
                            <h5>What it is worth</h5>
                            <p class="note">
                                @if (s.value > 0) {<b>{{ count(s.value) }}</b> of value a year. }
                                @if (s.revenue > 0) {<b>{{ money(s.revenue) }}</b> of revenue a year. }
                                Its states take <b>{{ hours(s.hours) }} hours</b>{{ s.cost ? ' and ' + money(s.cost) : '' }} a year{{ s.items ? ', for ' + count(s.items) + ' items' : '' }}.
                                @if (s.state === 'Dead end') {Nothing uses it beyond moving it between its states, so that work is friction unless it has a value the model doesn't show.}
                            </p>
                            @if (s.heldOutside) {<p class="note"><b>{{ s.heldOutside }} of its {{ s.states.length }} states shown {{ s.heldOutside === 1 ? 'is' : 'are' }} held outside a system with controls around it</b>, so a copy can be lost, or used as if it were the real thing.</p>}
                        </div>
                        <div>
                            <h5>Its states</h5>
                            @if (s.states.length) {
                            <ul class="steps">@for (x of s.states; track x.key) {<li>
                                @if (x.output) {<button type="button" class="link" (click)="selectOutput(x.key)" matTooltip="Show it in the outputs below">{{ x.name }}</button>} @else {<span>{{ x.name }}</span>}
                                <span class="muted">{{ x.output ? 'output' : 'passed between steps' }} · {{ x.model }} · {{ heldKindLabel(x.held) }}</span>
                                <app-edit-in-model [name]="x.name" (edit)="openNode(x.key, x.modelKey)"></app-edit-in-model></li>}</ul>
                            } @else {<p class="none">None of its states are in the graph shown.</p>}
                        </div>
                    </div>
                </div>
                }
            </section>
            }

            <!-- information that looks like the states of one asset, to link -->
            @if (canApply && visibleLinks.length) {
            <section class="panel links" aria-labelledby="info-links" data-testid="information-link-suggestions">
                <h3 id="info-links">Suggested asset links</h3>
                <p class="note">Information whose names say it is the same information at different stages. Link it to an asset to value it across all its states. LINQ reads only the names, so check each before linking.</p>
                @if (linkError) {<p class="status error" role="alert">{{ linkError }}</p>}
                <ul class="link-list">
                    @for (s of visibleLinks.slice(0, linksShown); track s.id) {
                    <li data-testid="information-link">
                        <div class="link-text">
                            <span>@if (s.kind === 'existing') {Link {{ s.nodes.length === 1 ? 'this' : 'these ' + s.nodes.length }} to the asset <b>{{ s.assetName }}</b>}
                            @else {<span>{{ s.nodes.length }} pieces of information in {{ s.models }} models look the same: create the asset <b>{{ s.assetName }}</b> and link them</span>}</span>
                            <span class="muted">{{ linkNodesText(s) }}</span>
                        </div>
                        <div class="link-actions">
                            <button mat-stroked-button color="accent" (click)="acceptLink(s)" [disabled]="applying" data-testid="information-link-accept">{{ s.kind === 'existing' ? 'Link' : 'Create and link' }}</button>
                            <button mat-button (click)="dismissLink(s)" [disabled]="applying" data-testid="information-link-dismiss">Not the same</button>
                        </div>
                    </li>
                    }
                </ul>
                @if (visibleLinks.length > linksShown) {<button mat-button color="accent" (click)="linksShown = visibleLinks.length">Show all {{ visibleLinks.length }}</button>}
            </section>
            }

            <!-- the outputs -->
            <section class="panel outputs" aria-labelledby="info-outputs">
                <div class="head">
                    <div>
                        <h3 id="info-outputs">Outputs</h3>
                        <p class="note">Each output's set value, derived value and what it rests on. Pick one to see what produces and uses it.</p>
                    </div>
                    @if (canApply) {
                    <div class="suggest" role="status" data-testid="information-suggest">
                        @switch (suggestState) {
                            @case ('loading') { <mat-spinner diameter="16"></mat-spinner><span>Asking AI for values…</span> }
                            @case ('ready') {
                                <span class="ai-tag">AI suggestions</span>
                                @if (pendingSuggestions.length) {<button mat-stroked-button color="accent" (click)="applyAllSuggestions()" [disabled]="applying" data-testid="information-use-all">Use all ({{ pendingSuggestions.length }})</button>}
                                <button mat-button color="accent" (click)="suggestValues(true)" [disabled]="applying">Regenerate</button>
                            }
                            @case ('failed') { <span class="muted">AI couldn't suggest values.</span><button mat-button color="accent" (click)="suggestValues(true)">Try again</button> }
                            @default {
                                <button mat-stroked-button color="accent" (click)="suggestValues()" [disabled]="!evidence" matTooltip="AI weighs each output's evidence and suggests a value per item with its reasoning" data-testid="information-suggest-values">
                                    <mat-icon>auto_awesome</mat-icon>Suggest values with AI</button>
                            }
                        }
                        @if (applying) {<span class="muted">Saving the values and rescoring…</span>}
                    </div>
                    }
                </div>
                @if (applyError) {<p class="status error" role="alert">{{ applyError }}</p>}
                <div class="scroll">
                    <table class="list" data-testid="information-table">
                        <thead><tr>
                            <th [attr.aria-sort]="outputSort.aria('name')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'name', label: 'Output', sorter: outputSort}"></ng-container></th>
                            <th class="optional" [attr.aria-sort]="outputSort.aria('model')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'model', label: 'Model', sorter: outputSort}"></ng-container></th>
                            <th class="num" [attr.aria-sort]="outputSort.aria('items')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'items', label: 'Items a year', sorter: outputSort}"></ng-container></th>
                            <th class="num" [attr.aria-sort]="outputSort.aria('value')" matTooltip="Value per item set on the output"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'value', label: 'Set value', sorter: outputSort}"></ng-container></th>
                            <th class="num" [attr.aria-sort]="outputSort.aria('revenuePerItem')" matTooltip="Revenue per item set on the output"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'revenuePerItem', label: 'Set revenue', sorter: outputSort}"></ng-container></th>
                            <th class="num derived" [attr.aria-sort]="outputSort.aria('derived')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'derived', label: 'Derived value', sorter: outputSort}"></ng-container></th>
                            <th class="optional" [attr.aria-sort]="outputSort.aria('held')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'held', label: 'Held as', sorter: outputSort}"></ng-container></th>
                            @if (suggestions) {<th class="num" [attr.aria-sort]="outputSort.aria('suggested')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'suggested', label: 'AI suggests', sorter: outputSort}"></ng-container></th>}
                            <th class="num optional" [attr.aria-sort]="outputSort.aria('revenue')" matTooltip="Revenue a year, from its own revenue per item and what flows back to it"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'revenue', label: 'Revenue', sorter: outputSort}"></ng-container></th>
                            <th class="num optional" [attr.aria-sort]="outputSort.aria('uses')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'uses', label: 'Uses', sorter: outputSort}"></ng-container></th>
                            <th class="num optional" [attr.aria-sort]="outputSort.aria('cost')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'cost', label: 'Cost to produce', sorter: outputSort}"></ng-container></th>
                            <th [attr.aria-sort]="outputSort.aria('state')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'state', label: 'State', sorter: outputSort}"></ng-container></th>
                        </tr></thead>
                        <tbody>
                            @for (o of sortedOutputs; track o.key) {
                            <tr [class.selected]="o.key === selectedKey" (click)="select(o)" [attr.data-testid]="'information-row-' + o.key">
                                <td class="first"><span class="with-context">{{ o.name }}@if (assetName(o); as an) {<span class="asset-tag" [matTooltip]="'A state of the information asset ' + an">{{ an }}</span>} @if (contextScope) {<app-context-graph-button [organisation]="linqset!.organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: 'output', key: o.key, name: o.name}}" [name]="o.name"></app-context-graph-button>}<app-edit-in-model [name]="o.name" (edit)="openNode(o.key, o.modelKey)"></app-edit-in-model></span></td>
                                <td class="optional model">{{ o.model }}</td>
                                <td class="num">{{ count(o.items) }}</td>
                                <td class="num">
                                    @if (o.userValue > 0) {
                                        {{ count(o.userValue) }}
                                        @if (o.valueSource) {<span class="src" [matTooltip]="o.valueSource === 'ai' ? 'Accepted from AI\\'s suggestion' : 'Accepted from LINQ\\'s derived value'">{{ o.valueSource === 'ai' ? 'AI' : 'LINQ' }}</span>}
                                    } @else if (o.joinedOut) {<span class="muted" matTooltip="Joined into another model: its value comes from the outputs it leads to there">joined</span>
                                    } @else {<span class="muted">–</span>}
                                </td>
                                <td class="num">
                                    @if (o.userRevenue > 0) {{{ money(o.userRevenue) }}
                                    } @else if (o.joinedOut) {<span class="muted" matTooltip="Joined into another model: its revenue comes from the outputs it leads to there">joined</span>
                                    } @else {<span class="muted">–</span>}
                                </td>
                                <td class="num derived">
                                    <span class="bar"><span class="mini"><span class="fill" [style.width.%]="o.derivedValue"></span></span><span class="mini-value">{{ o.derivedValue > 0 ? o.derivedValue : '–' }}</span></span>
                                    @if (canApply && !o.joinedOut && o.derivedValue > 0 && o.userValue !== o.derivedValue) {
                                    <button type="button" class="use" (click)="useDerived(o); $event.stopPropagation()" [disabled]="applying" matTooltip="Set the derived value as the output's value per item">Use</button>
                                    }
                                </td>
                                <td class="optional held" [class.outside]="isOutside(o)" [matTooltip]="heldTip(o)">{{ heldLabel(o) }}</td>
                                @if (suggestions) {
                                <td class="num">
                                    @if (suggestionFor(o); as s) {
                                        <span [matTooltip]="s.rationale">{{ s.value }}</span>
                                        @if (canApply && s.value !== o.userValue) {<button type="button" class="use" (click)="useSuggestion(o); $event.stopPropagation()" [disabled]="applying" matTooltip="Set AI's suggestion as the output's value per item">Use</button>}
                                    } @else {<span class="muted">–</span>}
                                </td>
                                }
                                <td class="num optional">{{ o.revenue ? money(o.revenue) : '–' }}</td>
                                <td class="num optional" [matTooltip]="uses(o)">{{ o.consumers }}{{ o.consumerModels ? ' · ' + o.consumerModels + 'm' : '' }}</td>
                                <td class="num optional">{{ o.cost ? money(o.cost) : hours(o.hours) + ' h' }}</td>
                                <td><span class="state" [class]="'state ' + stateClass(o)">{{ o.state }}</span></td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>

                <!-- the chosen output -->
                @if (selected; as o) {
                <div class="drill" data-testid="information-detail">
                    <div class="drill-head">
                        <h4>{{ o.name }} <span class="muted">· {{ o.model }}</span></h4>
                        <button type="button" class="link" (click)="openNode(o.key, o.modelKey)">Open in the model<mat-icon>open_in_new</mat-icon></button>
                    </div>
                    <div class="drill-body">
                        <div>
                            <h5>Its derived value, {{ o.derivedValue > 0 ? o.derivedValue + ' of 100' : 'none' }}</h5>
                            <p class="note">{{ reasons(o) }}</p>
                            @if (suggestionFor(o); as s) {<p class="note"><b>AI suggests {{ s.value }}:</b> {{ s.rationale }}</p>}
                            <h5>What it is worth</h5>
                            <p class="note">
                                @if (o.userValue > 0) {Value per item <b>{{ count(o.userValue) }}</b>{{ o.valueSource ? ' (accepted from ' + (o.valueSource === 'ai' ? 'AI' : 'LINQ') + ')' : '' }}, }
                                @if (o.value > 0) {<b>{{ count(o.value) }}</b> of value a year. }
                                @if (o.revenue > 0) {<b>{{ money(o.revenue) }}</b> of revenue a year. }
                                It takes <b>{{ hours(o.hours) }} hours</b>{{ o.cost ? ' and ' + money(o.cost) : '' }} a year to produce{{ o.items ? ' ' + count(o.items) + ' items' : '' }}.
                                @if (o.state === 'Dead end') {Nothing uses it, so that work is friction: either stop producing it, route it to where it creates value, or set the value it has that the model doesn't show.}
                                @if (o.joinedOut) {It is joined into another model, so it has no value of its own: it takes the value of the outputs it leads to there.}
                                @if (o.state === 'In use' && !o.joinedOut) {It is in use but no one has set its value, so its worth rests on the derived value{{ canApply ? ': use it, or set your own on the output' : '' }}.}
                            </p>
                            @if (canApply) {
                            <h5>Put it right</h5>
                            <div class="fixes" data-testid="information-fixes">
                                <span>Held as <app-held-as-fix [type]="o.type" [read]="o.typeRead" [name]="o.name" [disabled]="applying" (changed)="setHeldAs(o, $event)"></app-held-as-fix></span>
                                @if (!o.joinedOut) {<app-value-fix [name]="o.name" [label]="o.userValue > 0 ? 'Change its value' : 'Set its value'" [disabled]="applying" (set)="setValue(o, $event)"></app-value-fix>}
                            </div>
                            }
                        </div>
                        <div>
                            <h5>Produced by</h5>
                            @if (o.producedBy.length) {
                            <ul class="steps">@for (s of o.producedBy; track s.key) {<li><button type="button" class="link" (click)="openNode(s.key, s.modelKey)">{{ s.name }}<mat-icon>open_in_new</mat-icon></button><span class="muted">{{ s.model }}</span></li>}</ul>
                            } @else {<p class="none">No action in the graph shown creates it.</p>}
                            <h5>Used by</h5>
                            @if (o.usedBy.length) {
                            <ul class="steps">@for (s of o.usedBy; track s.key) {<li><button type="button" class="link" (click)="openNode(s.key, s.modelKey)">{{ s.name }}<mat-icon>open_in_new</mat-icon></button><span class="muted">{{ s.outcome ? 'outcome' : s.model }}</span></li>}</ul>
                            } @else if (o.consumers > 0) {<p class="none">Used in {{ o.consumers }} {{ o.consumers === 1 ? 'place' : 'places' }} outside the models shown.</p>
                            } @else {<p class="none">Nothing uses it.</p>}
                        </div>
                    </div>
                </div>
                }
            </section>

            <!-- the outcomes the outputs realise -->
            @if (a.outcomes.length) {
            <section class="panel outcomes" aria-labelledby="info-outcomes" data-testid="information-outcomes">
                <h3 id="info-outcomes">Outcomes</h3>
                <p class="note">The outcomes the outputs realise, with the value and revenue set on each. What is set on an outcome flows back to the outputs realising it and the work producing them.</p>
                <div class="scroll">
                    <table class="list" data-testid="information-outcome-table">
                        <thead><tr>
                            <th [attr.aria-sort]="outcomeSort.aria('name')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'name', label: 'Outcome', sorter: outcomeSort}"></ng-container></th>
                            <th class="optional" [attr.aria-sort]="outcomeSort.aria('model')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'model', label: 'Model', sorter: outcomeSort}"></ng-container></th>
                            <th class="num" [attr.aria-sort]="outcomeSort.aria('items')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'items', label: 'Items a year', sorter: outcomeSort}"></ng-container></th>
                            <th class="num" [attr.aria-sort]="outcomeSort.aria('value')" matTooltip="Value per item set on the outcome"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'value', label: 'Set value', sorter: outcomeSort}"></ng-container></th>
                            <th class="num" [attr.aria-sort]="outcomeSort.aria('revenuePerItem')" matTooltip="Revenue per item set on the outcome"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'revenuePerItem', label: 'Set revenue', sorter: outcomeSort}"></ng-container></th>
                            <th class="num optional" [attr.aria-sort]="outcomeSort.aria('valueYear')" matTooltip="Value a year: its value per item times the items realised"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'valueYear', label: 'Value', sorter: outcomeSort}"></ng-container></th>
                            <th class="num optional" [attr.aria-sort]="outcomeSort.aria('revenue')" matTooltip="Revenue a year: its revenue per item times the items realised"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'revenue', label: 'Revenue', sorter: outcomeSort}"></ng-container></th>
                            <th class="optional" [attr.aria-sort]="outcomeSort.aria('realisedBy')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'realisedBy', label: 'Realised by', sorter: outcomeSort}"></ng-container></th>
                            <th class="num optional" [attr.aria-sort]="outcomeSort.aria('cost')" matTooltip="The cost a year of all the work leading to it"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'cost', label: 'Cost to realise', sorter: outcomeSort}"></ng-container></th>
                            <th [attr.aria-sort]="outcomeSort.aria('state')"><ng-container *ngTemplateOutlet="sortHeader; context: {$implicit: 'state', label: 'State', sorter: outcomeSort}"></ng-container></th>
                        </tr></thead>
                        <tbody>
                            @for (o of sortedOutcomes; track o.key) {
                            <tr class="static" [attr.data-testid]="'information-outcome-' + o.key">
                                <td class="first"><span class="with-context">{{ o.name }}<app-edit-in-model [name]="o.name" (edit)="openNode(o.key, o.modelKey)"></app-edit-in-model></span></td>
                                <td class="optional model">{{ o.model }}</td>
                                <td class="num">{{ count(o.items) }}</td>
                                <td class="num">@if (o.userValue > 0) {{{ count(o.userValue) }}} @else {<span class="muted">–</span>}</td>
                                <td class="num">@if (o.userRevenue > 0) {{{ money(o.userRevenue) }}} @else {<span class="muted">–</span>}</td>
                                <td class="num optional">{{ o.value ? count(o.value) : '–' }}</td>
                                <td class="num optional">{{ o.revenue ? money(o.revenue) : '–' }}</td>
                                <td class="optional realised">
                                    @for (s of o.realisedBy; track s.key; let last = $last) {<button type="button" class="link" (click)="selectOutput(s.key)" matTooltip="Show it in the outputs">{{ s.name }}</button>{{ last ? '' : ', ' }}}
                                    @empty {<span class="muted">–</span>}
                                </td>
                                <td class="num optional">{{ o.cost ? money(o.cost) : hours(o.hours) + ' h' }}</td>
                                <td><span [class]="'state ' + (o.state === 'Not valued' ? 'state-dead' : 'state-good')">{{ o.state }}</span></td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </section>
            }

            <!-- each table header sorts its table by that column; clicking it again reverses the order -->
            <ng-template #sortHeader let-column let-label="label" let-sorter="sorter">
                <button type="button" class="sort" (click)="sorter.by(column)" [class.sorted]="sorter.column === column" [attr.aria-label]="'Sort by ' + label">{{ label }}<mat-icon>{{ sorter.icon(column) }}</mat-icon></button>
            </ng-template>
            }
        </div>
        }
    `,
    styles: [`
        .with-context { display: inline-flex; align-items: center; gap: 2px; }
        .fixes { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; font-size: 12px; color: var(--ia-muted); }
        .status { display: flex; align-items: center; gap: 10px; margin: 16px 0; color: var(--ia-muted); }
        .error { color: #9b1c1c; }
        :host { display: block; --ia-ink: var(--ink, #1E2233); --ia-muted: var(--slate, #5b6172); --ia-grey: var(--grey, #9aa0b0); --ia-line: var(--line, #e7e9f0);
            --ia-bg: var(--bg, #f4f5f9); --ia-surface: var(--paper, #fff); --ia-teal: var(--teal, #00a9a5); --ia-teal-soft: var(--teal-soft, #e0f5f4); --ia-teal-dark: #00706d; }
        .info { display: flex; flex-direction: column; gap: 16px; padding: 0 0 24px; color: var(--ia-ink); container-type: inline-size; container-name: info; }
        .intro { margin: 0; color: var(--ia-muted); font-size: 13px; line-height: 1.5; max-width: 100ch; }
        h3 { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
        h4 { margin: 0; font-size: 14px; font-weight: 600; }
        h5 { margin: 10px 0 4px; font-size: 12.5px; font-weight: 600; color: var(--ia-muted); text-transform: uppercase; letter-spacing: .3px; }
        .note { margin: 0 0 10px; color: var(--ia-muted); font-size: 12.5px; line-height: 1.45; max-width: 90ch; }
        .none { margin: 6px 0 0; color: var(--ia-muted); font-size: 13px; }
        .muted { color: var(--ia-grey); }
        .panel { min-width: 0; padding: 14px 16px 12px; border: 1px solid var(--ia-line); border-radius: 10px; background: var(--ia-surface); }
        .empty { display: flex; gap: 14px; align-items: flex-start; }
        .empty mat-icon { flex: none; width: 32px; height: 32px; font-size: 32px; color: var(--ia-grey); }
        .empty p { margin: 4px 0 0; color: var(--ia-muted); font-size: 13px; line-height: 1.5; max-width: 70ch; }

        /* the headline figures */
        .tiles { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
        @container info (max-width: 980px) { .tiles { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @container info (max-width: 640px) { .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        .tile { display: flex; flex-direction: column; gap: 2px; min-width: 0; padding: 12px 14px; border: 1px solid var(--ia-line); border-left-width: 4px; border-left-color: #94a3b8; border-radius: 10px; background: var(--ia-surface); }
        td.held { white-space: nowrap; color: var(--ia-muted); } td.held.outside { color: inherit; font-weight: 500; }
        .tile.good { border-left-color: #0ca30c; } .tile.warn { border-left-color: #fab219; } .tile.bad { border-left-color: #e34948; }
        .tile .label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ia-muted); }
        .tile .label mat-icon { width: 16px; height: 16px; font-size: 16px; color: var(--ia-grey); }
        /* a project's figure against the current state's */
        .chg { align-self: flex-start; margin-top: 4px; font-size: 11px; font-weight: 700; white-space: nowrap; padding: 1px 6px; border-radius: 6px; }
        .chg.good { background: #e4f6ec; color: #1e7b45; }
        .chg.bad { background: #fbe4e2; color: #b42318; }
        .chg.flat { background: #eef0f6; color: #6b7280; }
        .tile strong { font-size: 22px; font-weight: 600; line-height: 1.25; font-variant-numeric: tabular-nums; }
        .tile .of { font-size: 14px; font-weight: 500; color: var(--ia-muted); }
        .tile .sub { font-size: 12px; color: var(--ia-muted); line-height: 1.35; }

        .held-panel .held-table { width: 100%; }
        .held-table tr.outside td.first { color: #8a5a00; font-weight: 600; }
        .read { color: var(--ia-muted); font-size: 11.5px; }
        .body { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; align-items: start; }
        @container info (max-width: 820px) { .body { grid-template-columns: minmax(0, 1fr); } }
        ul { list-style: none; margin: 0; padding: 0; }
        /* the names take the room the longest needs, wrapping rather than being cut off when there is too little, and the
           bars line up across the rows in what is left */
        .signal-list, .holder-list { display: grid; grid-template-columns: minmax(140px, max-content) minmax(48px, 1fr) minmax(110px, auto); column-gap: 10px; }
        .signal-list li, .holder-list li { display: grid; grid-column: 1 / -1; grid-template-columns: subgrid; gap: 10px; align-items: center; padding: 6px 0; border-top: 1px solid var(--ia-line); font-size: 13px; }
        .signal-head, .holder-head { display: flex; flex-wrap: wrap; align-items: center; gap: 2px 8px; min-width: 0; }
        .signal-head strong, .holder-head strong { min-width: 0; overflow-wrap: anywhere; font-weight: 600; }
        .weight { flex: none; padding: 0 7px; border-radius: 8px; background: var(--ia-bg); color: var(--ia-muted); font-size: 11.5px; font-weight: 600; }
        .kind { flex: none; padding: 1px 7px; border-radius: 8px; background: #eef1f7; color: #3b4a6b; font-size: 11px; font-weight: 600; }
        .kind-system { background: #e8f1fb; color: #1d4f8a; }
        .track { position: relative; height: 6px; border-radius: 3px; background: var(--ia-bg); overflow: hidden; }
        .track .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 3px; background: var(--ia-teal); }
        .leader { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: 12.5px; }
        .amount { font-size: 12.5px; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .link { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: none; font: inherit; font-weight: 500; color: var(--ia-teal-dark); cursor: pointer; text-align: left; }
        .link mat-icon { width: 14px; height: 14px; font-size: 14px; opacity: 0; transition: opacity .15s; }
        .link:hover { text-decoration: underline; } .link:hover mat-icon { opacity: 1; }

        /* the outputs */
        .head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .suggest { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 12.5px; color: var(--ia-muted); }
        .ai-tag { padding: 2px 8px; border-radius: 6px; background: var(--purple-soft, #efe9fb); color: var(--purple, #5b3fd3); font-size: 10.5px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase; }
        .scroll { overflow-x: auto; max-width: 100%; }
        .list { width: 100%; min-width: 560px; border-collapse: collapse; }
        .list th { height: 36px; padding: 0 10px; background: var(--ia-bg); border-bottom: 1px solid var(--ia-line); color: var(--ia-muted); font-size: 12px; font-weight: 600; text-align: left; white-space: nowrap; }
        .list th.num { text-align: right; }
        .sort { display: inline-flex; align-items: center; gap: 2px; padding: 0; border: 0; background: none; color: inherit; font: inherit; cursor: pointer; white-space: nowrap; }
        .sort mat-icon { width: 14px; height: 14px; font-size: 14px; opacity: .35; }
        .sort:hover mat-icon, .sort.sorted mat-icon { opacity: 1; }
        .sort.sorted { color: var(--ia-ink); }
        .list td { height: 40px; padding: 4px 10px; border-bottom: 1px solid var(--ia-line); vertical-align: middle; font-size: 13px; }
        .list tbody tr { cursor: pointer; }
        .list tbody tr.static { cursor: default; }
        .list td.realised { max-width: 320px; }
        .list td.realised .link { display: inline; padding: 0; font-size: 13px; }
        .list tbody tr:hover td { background: #f8f9fb; }
        .list tbody tr.selected td { background: var(--ia-teal-soft); }
        .list td.first { font-weight: 500; }
        .list td.model { white-space: nowrap; color: var(--ia-muted); }
        .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .derived .bar { display: inline-flex; align-items: center; gap: 6px; }
        .mini { display: inline-block; position: relative; width: 70px; height: 8px; background: var(--ia-bg); border-radius: 4px; vertical-align: middle; overflow: hidden; }
        .mini .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 4px; background: var(--ia-teal); }
        .mini-value { min-width: 24px; text-align: right; font-variant-numeric: tabular-nums; }
        .src { margin-left: 6px; padding: 0 5px; border-radius: 6px; background: var(--ia-bg); color: var(--ia-muted); font-size: 10.5px; font-weight: 700; }
        .use { margin-left: 6px; padding: 0; border: 0; background: none; font: inherit; font-size: 11.5px; font-weight: 700; color: var(--ia-teal-dark); cursor: pointer; }
        .use:hover { text-decoration: underline; } .use:disabled { color: var(--ia-grey); cursor: default; }
        .state { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 12px; font-weight: 500; white-space: nowrap; background: var(--ia-bg); color: var(--ia-muted); }
        .state-good { background: var(--ia-teal-soft); color: var(--ia-teal-dark); }
        .state-use { background: #fdf3dd; color: #8a5a00; }
        .state-dead { background: #fbe4e4; color: #9b1c1c; }
        @container info (max-width: 1100px) { .optional { display: none; } }

        /* the chosen output */
        .drill { margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--ia-line); }
        .drill-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
        .drill-body { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 20px; }
        @container info (max-width: 820px) { .drill-body { grid-template-columns: minmax(0, 1fr); } }
        .risk { width: 16px; height: 16px; font-size: 16px; margin-left: 4px; color: #c27c00; vertical-align: middle; }
        .risk-note { display: flex; align-items: center; gap: 6px; color: #8a5a00; }
        .risk-note mat-icon, .risks mat-icon { width: 16px; height: 16px; font-size: 16px; color: #c27c00; }
        .risks li { display: flex; align-items: center; gap: 6px; padding: 2px 0; font-size: 13px; color: #8a5a00; }
        .register b { color: var(--ia-ink); font-weight: 600; }
        .link-list li { display: flex; align-items: center; justify-content: space-between; gap: 8px 16px; flex-wrap: wrap; padding: 8px 0; border-top: 1px solid var(--ia-line); font-size: 13px; }
        .link-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1 1 320px; }
        .link-text .muted { font-size: 12px; }
        .link-actions { display: flex; gap: 6px; flex: none; }
        .asset-tag { margin: 0 4px; padding: 0 6px; border-radius: 6px; background: #efe9fb; color: #5b3fd3; font-size: 10.5px; font-weight: 600; white-space: nowrap; }
        .steps li { display: flex; align-items: center; gap: 10px; padding: 3px 0; font-size: 13px; }
    `],
})
export class InformationAnalysisComponent implements OnChanges {
    @Input() linqset?: Linqset;
    @Input() organisationLinqset?: Linqset;          // the organisation's roles and systems, with their insights
    @Input() trigger: any;                           // the insights were refreshed
    // in a project, the current state's nodes and its roles and systems, for the figures to be compared with
    @Input() comparison?: Linqset;
    @Input() comparisonOrganisationLinqset?: Linqset;
    @Output() valuesApplied = new EventEmitter<void>();   // output values were set and the insights rescored, so what's shown is stale

    loading = false;
    error = '';
    analysis?: InformationAnalysis;
    baseline?: InformationAnalysis;                  // the current state's, when a project is looked at
    evidence?: EpmDecisionEvidence;                  // the scorecard's evidence, for AI's suggestions
    selectedKey = '';
    selectedAssetKey = '';
    linksShown = 5;
    linkError = '';
    private dismissedLinks = new Set<string>();     // suggested links the user said aren't the same information
    holdersShown = 8;
    maxWeight = 1;

    suggestions?: AIEpmValueSuggestion[];
    suggestState: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
    applying = false;
    applyError = '';

    // for the context graphs of its outputs and of the roles and systems the value lands on: the graph already loaded
    contextScope?: AnalysisScope;
    contextSource?: ContextSource;

    private request = 0;
    private suggestRequest = 0;
    private currency: Currency = getCurrency(DEFAULT_CURRENCY);

    constructor(private formatter: FormattingService, private insightsService: InsightsService, private aiServices: AIServices,
        private corrections: NodeCorrectionService, private dialog: MatDialog, private organisationService: OrganisationService, private download: DownloadService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['linqset'] || changes['trigger'] || changes['comparison']) void this.load();
    }

    // the outputs in what's being looked at, with their figures, from the analysis graph the other views use
    private async load() {
        const linqset = this.linqset;
        if (!linqset) { this.analysis = undefined; return; }
        const organisation = linqset.organisation;
        const scope = scopeForLinqset(linqset);
        const scoped = !isWholeOrganisation(scope);
        const modelKeys = new Set(scoped ? modelsInScope(scope, organisation).map(m => m._key) : []);
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        try {
            const comparison = this.comparison;
            const [graph, currentGraph] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(organisation, filterForScope(scope)) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>,
                comparison ? (this.insightsService.loadAnalysisGraphForFilter(organisation, filterForScope({ ...scope, workspaceKey: CURRENT_STATE_PROJECT_KEY })) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>)
                    .catch(err => { console.error('Unable to load the current state to compare with', err); return undefined; }) : undefined,
            ]);
            if (request !== this.request) return;
            // the current state over the same models; models made in the project aren't in it
            this.baseline = comparison && currentGraph ? this.analyse(comparison, this.comparisonOrganisationLinqset,
                scoped ? currentGraph.nodes.filter(n => modelKeys.has(n.modelKey)) : currentGraph.nodes,
                scoped ? currentGraph.edges.filter(e => modelKeys.has(e.modelKey)) : currentGraph.edges).analysis : undefined;
            this.contextScope = scope;
            this.contextSource = { graph, organisationModel: this.organisationLinqset ? { nodes: this.organisationLinqset.nodes, edges: this.organisationLinqset.edges } : undefined };
            const nodes = scoped ? graph.nodes.filter(n => modelKeys.has(n.modelKey)) : graph.nodes;
            const edges = scoped ? graph.edges.filter(e => modelKeys.has(e.modelKey)) : graph.edges;
            this.build(nodes, edges);
        } catch (err) {
            if (request !== this.request) return;
            console.error('Unable to value the information', err);
            this.error = "The information couldn't be valued. Please try again.";
        } finally {
            if (request === this.request) this.loading = false;
        }
    }

    private build(nodes: AnalysisNode[], edges: AnalysisEdge[]) {
        const { analysis, evidence } = this.analyse(this.linqset!, this.organisationLinqset, nodes, edges);
        this.evidence = evidence;
        this.analysis = analysis;
        this.maxWeight = Math.max(1, ...this.analysis.signals.map(s => s.weight));
        this.holdersShown = 8;
        // keep the output being looked at across a refresh, and the suggestions already made for these outputs
        if (!this.analysis.outputs.some(o => o.key === this.selectedKey)) this.selectedKey = '';
        if (!this.analysis.assets.some(a => a.key === this.selectedAssetKey)) this.selectedAssetKey = '';
        this.linksShown = 5;
        this.loadDismissedLinks();
    }

    // a headline figure against the current state's, when a project is looked at
    vs(figure: keyof ReturnType<typeof informationFigures>): Change | undefined {
        if (!this.baseline || !this.analysis) return undefined;
        return changeFrom(informationFigures(this.baseline.totals)[figure], informationFigures(this.analysis.totals)[figure], changeFormat(v => this.money(v), v => this.count(v)));
    }

    // the outputs of a workspace with their value, and the scorecard's evidence for AI's suggestions
    private analyse(linqset: Linqset, organisationLinqset: Linqset | undefined, nodes: AnalysisNode[], edges: AnalysisEdge[]): { analysis: InformationAnalysis; evidence?: EpmDecisionEvidence } {
        const organisation = linqset.organisation;
        this.currency = getCurrency(organisation.currencyCode || DEFAULT_CURRENCY);
        const modelNames = Object.fromEntries((organisation.models || []).map(m => [m._key, m.name]));

        // the roles and systems with the value their work creates in what's looked at
        const components: ValueHolder[] = linqset.nodes
            .filter(n => (n.superType === NODE_SUPERTYPE.ROLE && n.type !== NODE_TYPE.EXTERNAL_ORGANISATION) || n.superType === NODE_SUPERTYPE.SYSTEM)
            .map(n => {
                const i = getInsights(n, linqset);
                return { key: n._key, name: n.name, kind: n.superType === NODE_SUPERTYPE.ROLE ? 'Role' : 'System', value: Math.round(i?.contributedValue || 0), revenue: Math.round(i?.contributedRevenue || 0),
                    supported: i?.value || 0, outputsSupported: i?.outputsSupported || 0 } as ValueHolder;
            });

        // the EPM scorecard gives the evidence AI's suggestions are made from, and the friction in how information moves
        // (re-keying and manual bridges), so the friction here is the scorecard's
        let evidence: EpmDecisionEvidence | undefined;
        let informationFriction: InformationAnalysisInput['informationFriction'];
        // the organisation's information assets, with their figures in what's looked at
        const assets = assetInputs(organisation.informationAssets, linqset.insights, organisationLinqset?.nodes || linqset.nodes);
        if (organisationLinqset) {
            try {
                const scorecard = buildEpmScorecard({
                    scopeName: 'Information', workspaceName: linqset.workspaceName, modelKeys: [...new Set(nodes.map(n => n.modelKey))], nodes, edges,
                    components: epmComponents({ nodes: organisationLinqset.nodes, edges: organisationLinqset.edges }, linqset.workspaceKey),
                    modelNames, defaultCapacity: defaultCapacityHours(organisation) > 0, totals: { revenue: linqset.insights?.outputRevenue || 0, value: linqset.insights?.outputValue || 0 }, currency: this.currency.code,
                    assets, money: v => this.money(v), number: v => this.count(v),
                });
                evidence = scorecard.evidence;
                const t = scorecard.evidence.totals;
                informationFriction = {
                    rekeyCost: t.rekeyCost, rekeyHours: t.rekeyHours, rekeySteps: scorecard.rekeyRows.length,
                    bridgeCost: t.bridgeCost, bridgeHours: t.bridgeHours, bridges: scorecard.informationRisks.filter(r => r.kind === 'bridge' && r.alsoFriction).length,
                };
            } catch (err) {
                console.error('Unable to build the EPM scorecard for the information view', err);
            }
        }
        // each system's kind, to say how the information attached to it is held
        const systemKinds = Object.fromEntries((organisationLinqset?.nodes || linqset.nodes).filter(n => n.superType === NODE_SUPERTYPE.SYSTEM).map(n => [n._key, systemKindOf(n).kind]));
        const analysis = buildInformationAnalysis({
            nodes, edges, modelNames, components, informationFriction, systemKinds, assets,
            totals: { value: linqset.insights?.outputValue || 0, revenue: linqset.insights?.outputRevenue || 0 },
        });
        return { analysis, evidence };
    }

    heldLabel(o: InformationOutput) { return INFORMATION_HELD_LABELS[o.held] + (o.typeRead !== undefined ? " (LINQ's read)" : ''); }
    isOutside(o: InformationOutput) { return heldOutsideSystems(o.held); }
    heldTip(o: InformationOutput) {
        return o.held === 'unsaid' ? "Typed as plain information, so how it is held isn't said. Set its type to say"
            : heldOutsideSystems(o.held) ? (o.held === 'local' ? 'In a local tool, with no controls around who changes it: ' : 'Outside any system: ') + 'it can be lost, or found and used as if it were authoritative' : o.held === 'system' ? 'Typed as held in a system, but no system is attached to say which' : 'Held in a system';
    }
    percent(share: number) { return `${Math.round(share * 100)}%`; }
    heldOutsideText(t: InformationTotals): string {
        const h = t.heldOutside;
        const outside = h.outputs ? `${h.outputs} output${h.outputs === 1 ? '' : 's'} in local tools, files, on paper or by word of mouth` : 'none in local tools, files, on paper or by word of mouth';
        return h.unsaid ? `${outside}; ${h.unsaid} not said` : outside;
    }

    // friction as the scorecard splits it: dead ends, exception work on detours from the main flow, re-keying and the
    // manual bridges
    // the roles and systems are ranked by revenue contribution where there is any, else by value contribution
    private get byRevenue() { return !!this.analysis?.holders.some(h => h.revenue > 0); }

    // a role's or system's bar: its contribution against the largest
    holderWidth(h: ValueHolder) {
        const top = this.analysis?.holders[0];
        if (!top) return 0;
        return this.byRevenue ? (top.revenue ? h.revenue / top.revenue * 100 : 0) : (top.value ? h.value / top.value * 100 : 0);
    }

    // its contribution: revenue first where that is what they are ranked by, with value beside it
    holderAmount(h: ValueHolder) {
        const revenue = h.revenue ? this.money(h.revenue) : '', value = h.value ? `${this.count(h.value)} value` : '';
        return (this.byRevenue ? [revenue, value] : [value, revenue]).filter(x => x).join(' · ');
    }

    holderTip(h: ValueHolder) {
        const share = [h.revenue ? this.money(h.revenue) + ' of revenue' : '', h.value ? this.count(h.value) + ' value' : ''].filter(x => x).join(' and ');
        return `${h.name}: ${share} a year is its share of what its work creates.${h.supported ? ` It supports ${this.count(h.supported)} value: the full value of the outputs it works towards.` : ''}`;
    }

    heldKindLabel(h: InformationHeld) { return INFORMATION_HELD_LABELS[h]; }
    isHeldOutside(h: InformationHeld) { return heldOutsideSystems(h); }

    frictionSplit(t: InformationTotals): string {
        if (!t.deadEnds && !t.exceptionActions && !t.wasteActions && !t.rekeySteps && !t.bridges) return 'every output is used or valued, no work leaves the main flow, and nothing is re-keyed or carried between systems by hand';
        const amount = (cost: number, hrs: number) => (cost ? this.money(cost) : `${this.hours(hrs)} hours`);
        const parts: string[] = [];
        if (t.deadEnds) parts.push(`${amount(t.deadEndCost, t.deadEndHours)} on ${t.deadEnds} dead ${t.deadEnds === 1 ? 'end' : 'ends'}`);
        if (t.exceptionActions) parts.push(`${amount(t.exceptionCost, t.exceptionHours)} exception work on ${t.detours} ${t.detours === 1 ? 'detour' : 'detours'}`);
        if (t.wasteActions) parts.push(`${amount(t.wasteCost, t.wasteHours)} on ${t.wasteActions} ${t.wasteActions === 1 ? 'step' : 'steps'} not adding value`);
        if (t.rekeySteps) parts.push(`${amount(t.rekeyCost, t.rekeyHours)} re-keying in ${t.rekeySteps} ${t.rekeySteps === 1 ? 'step' : 'steps'}`);
        if (t.bridges) parts.push(`${amount(t.bridgeCost, t.bridgeHours)} on ${t.bridges} manual ${t.bridges === 1 ? 'bridge' : 'bridges'}`);
        return parts.join(' · ') + ' a year';
    }

    get selected(): InformationOutput | undefined {
        return this.analysis?.outputs.find(o => o.key === this.selectedKey);
    }

    select(o: InformationOutput) {
        this.selectedKey = this.selectedKey === o.key ? '' : o.key;
    }

    // ---------- sorting the outputs and outcomes tables by a column ----------

    // each table as LINQ lists it until a column is chosen
    outputSort = new TableSort<OutputSortColumn>(OUTPUT_TEXT_COLUMNS);
    outcomeSort = new TableSort<OutcomeSortColumn>(OUTCOME_TEXT_COLUMNS);

    get sortedOutcomes(): InformationOutcome[] {
        return this.outcomeSort.sort(this.analysis?.outcomes || [], (o, column) => {
            switch (column) {
                case 'name': return o.name;
                case 'model': return o.model;
                case 'items': return o.items;
                case 'value': return o.userValue;
                case 'revenuePerItem': return o.userRevenue;
                case 'valueYear': return o.value;
                case 'revenue': return o.revenue;
                case 'realisedBy': return o.realisedBy.length;
                case 'cost': return o.cost || o.hours;
                case 'state': return OUTCOME_STATE_ORDER.indexOf(o.state);
            }
        });
    }

    get sortedOutputs(): InformationOutput[] {
        return this.outputSort.sort(this.analysis?.outputs || [], (o, column) => {
            switch (column) {
                case 'name': return o.name;
                case 'model': return o.model;
                case 'items': return o.items;
                case 'value': return o.userValue;
                case 'revenuePerItem': return o.userRevenue;
                case 'derived': return o.derivedValue;
                case 'held': return this.heldLabel(o);
                case 'suggested': return this.suggestionFor(o)?.value ?? -1;
                case 'revenue': return o.revenue;
                case 'uses': return o.consumers * 1000 + o.consumerModels;
                case 'cost': return o.cost || o.hours;
                case 'state': return OUTPUT_STATE_ORDER.indexOf(o.state);
            }
        });
    }

    selectByName(name: string, model: string) {
        const o = this.analysis?.outputs.find(x => sameName(x.name, name) && sameName(x.model, model));
        if (o) this.selectedKey = o.key;
    }

    // values can be set on the current state, by those who can change it
    get canApply(): boolean {
        const linqset = this.linqset;
        return !!linqset && this.corrections.canCorrect(linqset.organisation, linqset.workspaceKey);
    }

    stateClass(o: { state: InformationState }) {
        return o.state === 'Dead end' ? 'state-dead' : o.state === 'In use' ? 'state-use' : 'state-good';
    }

    uses(o: InformationOutput) {
        const parts = [`${o.consumers} ${o.consumers === 1 ? 'action or outcome uses' : 'actions or outcomes use'} it directly`];
        if (o.consumerModels) parts.push(`used in ${o.consumerModels} other ${o.consumerModels === 1 ? 'model' : 'models'}`);
        if (o.outcomesReached) parts.push(`reaches ${o.outcomesReached} ${o.outcomesReached === 1 ? 'outcome' : 'outcomes'}`);
        return parts.join('; ');
    }

    // ---------- information assets ----------

    get selectedAsset(): InformationAssetSummary | undefined {
        return this.analysis?.assets.find(a => a.key === this.selectedAssetKey);
    }

    selectAsset(a: InformationAssetSummary) {
        this.selectedAssetKey = this.selectedAssetKey === a.key ? '' : a.key;
    }

    selectOutput(key: string) {
        this.selectedKey = key;
    }

    classLabel(a: InformationAssetSummary) {
        return a.classification ? ASSET_CLASSIFICATION_LABELS[a.classification] : 'not classified';
    }

    riskTip(a: InformationAssetSummary) {
        return a.risks.map(r => r.text).join('. ');
    }

    // changes the asset's register, and values the information again with it
    async editAsset(a: InformationAssetSummary) {
        const linqset = this.linqset;
        const asset = linqset?.organisation.informationAssets?.find(x => x._key === a.key);
        if (!linqset || !asset || !this.canApply) return;
        const saved = await editInformationAsset(this.dialog, this.organisationService, asset, this.organisationLinqset?.nodes || linqset.nodes);
        if (saved) void this.load();
    }

    // the register of the assets shown, with their figures, as a spreadsheet
    exportRegister() {
        const assets = this.analysis?.assets || [];
        const header = ['Asset', 'Description', 'Owner', 'Classification', 'System of record', 'States', 'Models', 'Items a year', 'Derived value', 'Value a year',
            'Revenue a year', 'Uses', 'Other models using it', 'Outcomes reached', 'Hours a year on its states', 'Cost a year of its states', 'States held outside systems', 'State', 'Risks'];
        const rows = assets.map(a => [a.name, a.description, a.owner || '', a.classification ? ASSET_CLASSIFICATION_LABELS[a.classification] : '', a.systemOfRecord || '',
            a.numberOfStates, a.models, Math.round(a.items), a.derivedValue ?? '', Math.round(a.value), Math.round(a.revenue), a.consumers, a.consumerModels, a.outcomesReached,
            Math.round(a.hours), Math.round(a.cost), a.heldOutside, a.state, a.risks.map(r => r.text).join('; ')]);
        this.download.downloadFile(toCsv(header, rows), 'LINQ information asset register', 'csv');
    }

    // ---------- suggested links to assets ----------

    // the suggestions the user hasn't turned down
    get visibleLinks(): AssetLinkSuggestion[] {
        return (this.analysis?.linkSuggestions || []).filter(s => !this.dismissedLinks.has(s.id));
    }

    linkNodesText(s: AssetLinkSuggestion) {
        const shown = s.nodes.slice(0, 6).map(n => `${n.name} (${n.model})`);
        return shown.join(', ') + (s.nodes.length > shown.length ? ` and ${s.nodes.length - shown.length} more` : '');
    }

    // links the information to the asset, creating it first for a new one, and has the insights worked out again
    async acceptLink(s: AssetLinkSuggestion) {
        const linqset = this.linqset;
        if (!linqset || !this.canApply || this.applying) return;
        this.linkError = '';
        let assetKey = s.assetKey;
        if (s.kind === 'new') {
            const created = await this.organisationService.createInformationAsset({ _key: v4(), name: s.assetName, description: '' });
            if (!created) { this.linkError = `The asset ${s.assetName} couldn't be created. Please try again.`; return; }
            assetKey = created._key;
        }
        await this.correct(s.nodes.map(n => ({ modelKey: n.modelKey, key: n.key, informationAssetKey: assetKey })), 'Link to the information asset');
    }

    // turns a suggestion down, so it isn't made again for this organisation in this browser
    dismissLink(s: AssetLinkSuggestion) {
        this.dismissedLinks.add(s.id);
        try {
            localStorage.setItem(this.dismissedKey, JSON.stringify([...this.dismissedLinks]));
        } catch { /* the suggestion is turned down for now, though not remembered */ }
    }

    private get dismissedKey() { return `linq.assetLinks.dismissed.${this.linqset?.organisation._key || ''}`; }

    private loadDismissedLinks() {
        try {
            this.dismissedLinks = new Set(JSON.parse(localStorage.getItem(this.dismissedKey) || '[]'));
        } catch {
            this.dismissedLinks = new Set();
        }
    }

    assetName(o: InformationOutput) {
        return o.assetKey ? this.analysis?.assets.find(a => a.key === o.assetKey)?.name : undefined;
    }

    statesTip(a: InformationAssetSummary) {
        return `${a.numberOfStates} ${a.numberOfStates === 1 ? 'state' : 'states'} in the models looked at${a.models > 1 ? ', in ' + a.models + ' models' : ''}`;
    }

    // how its states are held: how many are outside a controlled system where any are, else the one way the states that
    // say are held alike, with those that don't say left to the tooltip
    assetHeldLabel(a: InformationAssetSummary) {
        if (!a.states.length) return '–';
        if (a.heldOutside) return `${a.heldOutside} of ${a.states.length} outside systems`;
        const kinds = new Set(a.states.map(s => s.held).filter(h => h !== 'unsaid'));
        if (!kinds.size) return INFORMATION_HELD_LABELS.unsaid;
        return kinds.size === 1 ? INFORMATION_HELD_LABELS[[...kinds][0]] : 'In systems';
    }

    assetHeldTip(a: InformationAssetSummary) {
        if (!a.states.length) return 'None of its states are in the graph shown';
        const parts = HELD_ORDER.map(h => ({ h, n: a.states.filter(s => s.held === h).length })).filter(x => x.n).map(x => `${x.n} ${INFORMATION_HELD_LABELS[x.h].toLowerCase()}`);
        return 'Its states shown: ' + parts.join(', ') + (a.heldOutside ? '. Any state outside a controlled system puts the asset at risk' : '');
    }

    assetUses(a: InformationAssetSummary) {
        if (a.derivedValue === undefined) return 'How it is used is worked out with the insights';
        const parts = [`${a.consumers} ${a.consumers === 1 ? 'action or outcome uses' : 'actions or outcomes use'} it, beyond moving it between its states`];
        if (a.consumerModels) parts.push(`used in ${a.consumerModels} other ${a.consumerModels === 1 ? 'model' : 'models'}`);
        if (a.outcomesReached) parts.push(`reaches ${a.outcomesReached} ${a.outcomesReached === 1 ? 'outcome' : 'outcomes'}`);
        return parts.join('; ');
    }

    // what an asset's derived value rests on, in words
    assetReasons(a: InformationAssetSummary): string {
        if (a.derivedValue === undefined) return "LINQ works out an asset's derived value with the insights, so it shows once they are worked out again.";
        const r: string[] = [];
        if (a.revenuePerItem > 0) r.push(`${this.money(a.revenuePerItem)} of revenue reaches each item`);
        if (a.valuePerItem > 0) r.push(`${this.count(a.valuePerItem)} of value reaches each item`);
        if (a.outcomesReached > 0) r.push(`it reaches ${a.outcomesReached} ${a.outcomesReached === 1 ? 'outcome' : 'outcomes'}`);
        if (a.consumers > 0) r.push(`${a.consumers} ${a.consumers === 1 ? 'action or outcome uses' : 'actions or outcomes use'} it`);
        if (a.consumerModels > 0) r.push(`it is used in ${a.consumerModels} other ${a.consumerModels === 1 ? 'model' : 'models'}`);
        if (a.externalConsumers > 0) r.push(`${a.externalConsumers} external ${a.externalConsumers === 1 ? 'organisation works' : 'organisations work'} with it`);
        if (!r.length) return 'Nothing values or uses it beyond moving it between its states, so LINQ derives no value for it.';
        return `Read from the graph, across all its states: ${r.join(', ')}. Each signal is measured against the highest of the organisation's assets and weighted, revenue most.`;
    }

    // what an output's derived value rests on, in words
    reasons(o: InformationOutput): string {
        const r: string[] = [];
        if (o.revenuePerItem > 0) r.push(`${this.money(o.revenuePerItem)} of revenue reaches each item`);
        if (o.value > 0 && o.items > 0 && !(o.userValue > 0)) r.push(`${this.count(o.value / o.items)} of value set downstream reaches each item`);
        if (o.outcomesReached > 0) r.push(`it reaches ${o.outcomesReached} ${o.outcomesReached === 1 ? 'outcome' : 'outcomes'}`);
        if (o.consumers > 0) r.push(`${o.consumers} ${o.consumers === 1 ? 'action or outcome uses' : 'actions or outcomes use'} it directly`);
        if (o.consumerModels > 0) r.push(`it is used in ${o.consumerModels} other ${o.consumerModels === 1 ? 'model' : 'models'}`);
        if (o.externalConsumers > 0) r.push(`${o.externalConsumers} external ${o.externalConsumers === 1 ? 'organisation works' : 'organisations work'} with it`);
        if (!r.length) return 'Nothing values or uses it in the graph, so LINQ derives no value for it.';
        return `Read from the graph: ${r.join(', ')}. Each signal is measured against the organisation's highest and weighted, revenue most.`;
    }

    // ---------- AI suggestions ----------

    // none for an output joined into another model, which takes its value from there
    suggestionFor(o: InformationOutput): AIEpmValueSuggestion | undefined {
        if (o.joinedOut) return undefined;
        return this.suggestions?.find(s => sameName(s.name, o.name) && (!s.model || sameName(s.model, o.model)));
    }

    get pendingSuggestions(): { output: InformationOutput; suggestion: AIEpmValueSuggestion }[] {
        return (this.analysis?.outputs || [])
            .map(output => ({ output, suggestion: this.suggestionFor(output) as AIEpmValueSuggestion }))
            .filter(p => p.suggestion && p.suggestion.value !== p.output.userValue);
    }

    async suggestValues(regenerate = false) {
        const linqset = this.linqset;
        if (!linqset || !this.evidence || this.suggestState === 'loading' || (this.suggestState === 'ready' && !regenerate)) return;
        const request = ++this.suggestRequest;
        this.suggestState = 'loading';
        try {
            const suggestions = await this.aiServices.suggestEpmValues(linqset.organisation._key, linqset.workspaceKey, this.evidence);
            if (request !== this.suggestRequest) return;
            if (!suggestions.length) throw new Error('AI returned no usable value suggestions');
            this.suggestions = suggestions;
            this.suggestState = 'ready';
        } catch (err) {
            if (request !== this.suggestRequest) return;
            console.error('Unable to suggest output values with AI', err);
            this.suggestions = undefined;
            this.suggestState = 'failed';
        }
    }

    useSuggestion(o: InformationOutput) {
        const s = this.suggestionFor(o);
        if (s) void this.applyValues([{ output: o, value: s.value, source: OUTPUT_VALUE_SOURCE.AI }]);
    }

    applyAllSuggestions() {
        void this.applyValues(this.pendingSuggestions.map(p => ({ output: p.output, value: p.suggestion.value, source: OUTPUT_VALUE_SOURCE.AI })));
    }

    useDerived(o: InformationOutput) {
        void this.applyValues([{ output: o, value: o.derivedValue, source: OUTPUT_VALUE_SOURCE.DERIVED }]);
    }

    // sets the values on the outputs in their current state models, has the insights worked out again, and says so
    private applyValues(values: { output: InformationOutput; value: number; source: OUTPUT_VALUE_SOURCE | null }[]) {
        return this.correct(values.map(({ output, value, source }) =>
            ({ modelKey: output.modelKey, key: output.key, properties: { outputValue: value, outputValueSource: source } })), 'Save the output values');
    }

    // a value typed in, so it is the user's own rather than a suggestion accepted
    setValue(o: InformationOutput, value: number) {
        void this.applyValues([{ output: o, value, source: null }]);
    }

    // how the output is held is read from its type
    setHeldAs(o: InformationOutput, type: NODE_TYPE) {
        void this.correct([{ modelKey: o.modelKey, key: o.key, type }], 'Save how the output is held');
    }

    private async correct(changes: NodeCorrection[], what: string) {
        const linqset = this.linqset;
        if (!linqset || !this.canApply || this.applying || !changes.length) return;
        this.applying = true;
        this.applyError = '';
        try {
            await this.corrections.apply(linqset.organisation, changes, what);
            this.valuesApplied.emit();
        } catch (err) {
            console.error('Unable to change the outputs', err);
            this.applyError = "The change couldn't be saved. Please try again.";
        } finally {
            this.applying = false;
        }
    }

    // the node in its model, selected
    openNode(key: string, modelKey: string) {
        const linqset = this.linqset;
        if (linqset) this.corrections.openInModel(linqset.organisation, modelKey, key, linqset.workspaceKey);
    }

    money(value: number) {
        return this.formatter.formatAbbreviatedNumber(value, this.currency);
    }

    hours(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    count(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }
}
